(() => {
  const DATA = Array.isArray(window.CozyBeadPaletteData) ? window.CozyBeadPaletteData : [];
  const STORAGE_KEY = 'cozybead-color-tray-v1';
  const productCache = new Map();
  const mainImageCache = new Map();
  let cartEventsPromise;

  const init = (root) => {
    if (!DATA.length || root.dataset.cbpReady === 'true') return;
    root.dataset.cbpReady = 'true';

    const $ = (selector) => root.querySelector(selector);
    const $$ = (selector) => Array.from(root.querySelectorAll(selector));
    const elements = {
      search: $('[data-cbp-search]'),
      searchClear: $('[data-cbp-search-clear]'),
      families: $('[data-cbp-families]'),
      palette: $('[data-cbp-palette]'),
      empty: $('[data-cbp-empty]'),
      reset: $('[data-cbp-reset]'),
      resultCount: $('[data-cbp-result-count]'),
      selectionHint: $('[data-cbp-selection-hint]'),
      sizeButtons: $$('[data-cbp-size-switch] [data-size]'),
      selectionCount: $('[data-cbp-selection-count]'),
      productCard: $('[data-cbp-product-card]'),
      productImage: $('[data-cbp-product-image]'),
      imageLoading: $('[data-cbp-image-loading]'),
      productSwatch: $('[data-cbp-product-swatch]'),
      productFamily: $('[data-cbp-product-family]'),
      productTitle: $('[data-cbp-product-title]'),
      productCode: $('[data-cbp-product-code]'),
      selectedSize: $('[data-cbp-selected-size]'),
      pack: $('[data-cbp-pack]'),
      productPrice: $('[data-cbp-product-price]'),
      availability: $('[data-cbp-product-availability]'),
      quantity: $('[data-cbp-quantity]'),
      addColor: $('[data-cbp-add-color]'),
      productLink: $('[data-cbp-product-link]'),
      productError: $('[data-cbp-product-error]'),
      clearTray: $('[data-cbp-clear-tray]'),
      closeTray: $('[data-cbp-close-tray]'),
      trayEmpty: $('[data-cbp-tray-empty]'),
      trayList: $('[data-cbp-tray-list]'),
      subtotal: $('[data-cbp-subtotal]'),
      addCart: $('[data-cbp-add-cart]'),
      cartStatus: $('[data-cbp-cart-status]'),
      mobileBar: $('[data-cbp-mobile-bar]'),
      mobileDot: $('[data-cbp-mobile-dot]'),
      mobileCode: $('[data-cbp-mobile-code]'),
      mobileSize: $('[data-cbp-mobile-size]'),
      mobilePrice: $('[data-cbp-mobile-price]'),
      mobileTray: $('[data-cbp-mobile-tray]'),
      mobileAdd: $('[data-cbp-mobile-add]')
    };

    const colorByCode = new Map(DATA.map((color) => [color.code, color]));
    const isColorEnabled = (color, size) => Boolean(
      color
      && (color.sizes ? color.sizes[size] === true : color.variants?.[size]?.id)
      && color.variants?.[size]?.id
    );
    const colorsForSize = (size) => DATA.filter((color) => isColorEnabled(color, size));
    const familiesForSize = (size) => Array.from(
      new Map(colorsForSize(size).map((color) => [color.group, color.family])).entries()
    );
    const imageForColor = (color, size) => {
      if (!color) return '';
      if (size === 'midi' && color.code === 'G1') return root.dataset.cbpMidiG1Image || '';
      return color.images?.[size] || color.beadImage || color.image || '';
    };
    const familyRepresentatives = {
      A: 'A26',
      B: 'B1',
      C: 'C17',
      D: 'D13',
      E: 'E13',
      F: 'F5',
      G: 'G6',
      H: 'H7',
      M: 'M13'
    };
    const url = new URL(window.location.href);
    const initialCode = (url.searchParams.get('color') || 'A1').toUpperCase();
    const initialSize = url.searchParams.get('size') === 'midi' ? 'midi' : 'mini';
    const initialColors = colorsForSize(initialSize);

    const state = {
      family: 'all',
      search: '',
      code: initialColors.some((color) => color.code === initialCode) ? initialCode : (initialColors[0]?.code || 'A1'),
      size: initialSize,
      product: null,
      variant: null,
      request: 0,
      tray: loadTray()
    };

    function loadTray() {
      try {
        const parsed = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '[]');
        return new Map(parsed.flatMap((item) => {
          if (!item?.key || !item.variantId) return [];
          const color = colorByCode.get(item.code);
          const current = color?.variants?.[item.size];
          if (!isColorEnabled(color, item.size) || String(current.id) !== String(item.variantId)) return [];
          return [[item.key, { ...item, variantId: current.id, price: Number(current.price) }]];
        }));
      } catch (_) {
        return new Map();
      }
    }

    function saveTray() {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(state.tray.values())));
      } catch (_) {
        // Shopping still works if storage is unavailable.
      }
    }

    function money(cents) {
      const value = Number(cents || 0) / 100;
      const currency = root.dataset.currency || window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol'
        }).format(value);
      } catch (_) {
        return `$${value.toFixed(2)}`;
      }
    }

    function isLight(hex) {
      const value = hex.replace('#', '');
      const r = parseInt(value.slice(0, 2), 16);
      const g = parseInt(value.slice(2, 4), 16);
      const b = parseInt(value.slice(4, 6), 16);
      return (r * 299 + g * 587 + b * 114) / 1000 > 218;
    }

    function productEndpoint(handle) {
      const shopRoot = window.Shopify?.routes?.root || root.dataset.routesRoot || '/';
      return `${shopRoot.replace(/\/?$/, '/')}products/${handle}.js`;
    }

    function cartEvents() {
      cartEventsPromise ||= import('@shopify/events').catch(() => null);
      return cartEventsPromise;
    }

    async function addItems(items, source) {
      const shopRoot = window.Shopify?.routes?.root || root.dataset.routesRoot || '/';
      const cartItemsComponents = Array.from(document.querySelectorAll('cart-items-component'));
      const sectionIds = cartItemsComponents
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
      const events = await cartEvents();
      const EventClass = events?.CartLinesUpdateEvent;
      const deferred = EventClass?.createPromise();
      const existingCount = Number(document.querySelector('cart-icon')?.currentCartCount || 0);
      const itemCount = existingCount + items.reduce((sum, item) => sum + item.quantity, 0);

      if (EventClass && deferred) {
        const autoOpenDrawer = document.querySelector('cart-drawer-component[auto-open]');
        autoOpenDrawer?.removeAttribute('auto-open');
        document.dispatchEvent(
          new EventClass({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
            promise: deferred.promise
          })
        );
        autoOpenDrawer?.setAttribute('auto-open', '');
      }

      try {
        const response = await fetch(`${shopRoot.replace(/\/?$/, '/')}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Cart request failed');

        if (EventClass && deferred) {
          deferred.resolve({
            cart: EventClass.createCartFromAjaxResponse({
              ...payload,
              item_count: itemCount,
              currency: root.dataset.currency || window.Shopify?.currency?.active || 'USD'
            }),
            detail: {
              didError: false,
              itemCount,
              items: payload.items,
              sections: payload.sections,
              source
            }
          });
        } else {
          document.querySelectorAll('cart-icon').forEach((icon) => icon.renderCartBubble?.(itemCount));
        }
        return payload;
      } catch (error) {
        deferred?.reject(error);
        throw error;
      }
    }

    function resizedImage(url, width = 260) {
      if (!url) return '';
      try {
        const imageUrl = new URL(url, window.location.origin);
        imageUrl.searchParams.set('width', String(width));
        return imageUrl.toString();
      } catch (_) {
        return url;
      }
    }

    async function getProduct(color) {
      if (productCache.has(color.handle)) return productCache.get(color.handle);
      const request = fetch(productEndpoint(color.handle), {
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error(`Product request failed (${response.status})`);
        return response.json();
      });
      productCache.set(color.handle, request);
      try {
        return await request;
      } catch (error) {
        productCache.delete(color.handle);
        throw error;
      }
    }

    function variantFor(product, size) {
      const mini = (variant) => /2\.6|mini/i.test(`${variant.title} ${variant.option1 || ''}`);
      const midi = (variant) => /(^|\D)5(?:\.0)?\s*mm|midi/i.test(`${variant.title} ${variant.option1 || ''}`);
      return product.variants.find(size === 'mini' ? mini : midi) || null;
    }

    function productImage(product, variant) {
      const candidate = variant?.featured_image?.src || variant?.featured_image || product.featured_image || product.images?.[0];
      return typeof candidate === 'string' ? candidate : candidate?.src || '';
    }

    function preloadMainImage(url) {
      const source = resizedImage(url, 900);
      if (!source) return Promise.resolve('');
      if (mainImageCache.has(source)) return mainImageCache.get(source);

      const request = new Promise((resolve, reject) => {
        const image = new Image();
        image.decoding = 'async';
        image.onload = async () => {
          try {
            await image.decode?.();
          } catch (_) {
            // A completed load is still safe to display when decode() is unavailable or rejects.
          }
          resolve(source);
        };
        image.onerror = () => reject(new Error('Color image failed to load'));
        image.src = source;
      });

      mainImageCache.set(source, request);
      request.catch(() => mainImageCache.delete(source));
      return request;
    }

    async function showMainImage(url, color, request) {
      if (!url) return false;
      try {
        const source = await preloadMainImage(url);
        if (!source || request !== state.request) return false;
        if (elements.productImage.getAttribute('src') !== source) {
          elements.productImage.src = source;
        }
        elements.productImage.alt = `${color.code} MARD fuse beads close-up`;
        elements.productImage.hidden = false;
        elements.productImage.dataset.cbpImageCode = color.code;
        elements.imageLoading.hidden = true;
        return true;
      } catch (_) {
        if (request === state.request && elements.productImage.hidden) {
          elements.imageLoading.hidden = false;
          elements.imageLoading.textContent = 'Product image unavailable';
        }
        return false;
      }
    }

    function renderFamilyButtons() {
      const fragment = document.createDocumentFragment();
      const sizeColors = colorsForSize(state.size);
      const families = familiesForSize(state.size);
      const entries = [['all', 'All colors'], ...families];
      entries.forEach(([value, label]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cbp__family-button';
        button.dataset.family = value;
        button.setAttribute('aria-pressed', String(state.family === value));

        const media = document.createElement('span');
        media.className = 'cbp__family-media';
        const preferred = colorByCode.get(value === 'all' ? 'A17' : familyRepresentatives[value]);
        const representative = isColorEnabled(preferred, state.size)
          ? preferred
          : sizeColors.find((color) => value === 'all' || color.group === value);
        const source = imageForColor(representative, state.size);
        if (source) {
          const image = document.createElement('img');
          image.src = resizedImage(source, 360);
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.width = 360;
          image.height = 360;
          media.appendChild(image);
        }
        const solid = document.createElement('span');
        solid.className = 'cbp__family-solid';
        if (value === 'all') {
          solid.classList.add('cbp__family-solid--all');
          ['#eb7ca7', '#f3b968', '#7bc5aa', '#77bddd', '#9d8ed6', '#755371'].forEach((hex) => {
            const color = document.createElement('i');
            color.style.backgroundColor = hex;
            solid.appendChild(color);
          });
        } else {
          solid.style.backgroundColor = representative?.hex || '#edf4fa';
        }
        media.appendChild(solid);

        const copy = document.createElement('span');
        copy.className = 'cbp__family-copy';
        const series = document.createElement('small');
        series.textContent = value === 'all' ? `MARD ${sizeColors.length}` : `${value} series`;
        const name = document.createElement('strong');
        name.textContent = label;
        copy.append(series, name);
        button.append(media, copy);
        fragment.appendChild(button);
      });
      elements.families.replaceChildren(fragment);
    }

    function renderPalette() {
      const query = state.search.trim().toUpperCase();
      const sizeColors = colorsForSize(state.size);
      const families = familiesForSize(state.size);
      const matches = sizeColors.filter((color) => {
        const inFamily = state.family === 'all' || color.group === state.family;
        const inSearch = !query || `${color.code} ${color.family} ${color.group}`.toUpperCase().includes(query);
        return inFamily && inSearch;
      });

      const grouped = new Map();
      matches.forEach((color) => {
        if (!grouped.has(color.group)) grouped.set(color.group, []);
        grouped.get(color.group).push(color);
      });

      const fragment = document.createDocumentFragment();
      families.forEach(([group, family]) => {
        const colors = grouped.get(group);
        if (!colors?.length) return;
        const section = document.createElement('section');
        section.className = 'cbp__group';
        section.setAttribute('aria-labelledby', `cbp-group-${group}`);

        const heading = document.createElement('div');
        heading.className = 'cbp__group-heading';
        const title = document.createElement('h2');
        title.id = `cbp-group-${group}`;
        title.textContent = family;
        const count = document.createElement('span');
        count.textContent = `${colors.length} colors`;
        heading.append(title, count);

        const grid = document.createElement('div');
        grid.className = 'cbp__swatches';
        colors.forEach((color) => {
          const card = document.createElement('div');
          card.className = 'cbp__swatch-card';
          card.dataset.light = String(isLight(color.hex));

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cbp__swatch';
          button.dataset.selectCode = color.code;
          button.setAttribute('aria-label', `Select ${color.code}, ${family}`);
          button.setAttribute('aria-pressed', String(state.code === color.code));

          const media = document.createElement('span');
          media.className = 'cbp__swatch-media';
          media.style.setProperty('--swatch', color.hex);
          const source = imageForColor(color, state.size);
          if (source) {
            const image = document.createElement('img');
            image.src = resizedImage(source);
            image.alt = `${color.code} ${family} fuse beads close-up`;
            image.loading = 'lazy';
            image.decoding = 'async';
            image.width = 260;
            image.height = 260;
            media.appendChild(image);
          }
          const code = document.createElement('span');
          code.className = 'cbp__swatch-code';
          code.textContent = color.code;
          button.append(media, code);

          const quickAdd = document.createElement('button');
          const variant = color.variants?.[state.size];
          const sizeLabel = state.size === 'mini' ? '2.6mm Mini' : '5mm Midi';
          quickAdd.type = 'button';
          quickAdd.className = 'cbp__quick-add';
          quickAdd.dataset.quickAdd = color.code;
          quickAdd.setAttribute('aria-label', `Add ${color.code} ${sizeLabel} to selected colors`);
          quickAdd.disabled = variant?.available === false;
          quickAdd.innerHTML = '<span aria-hidden="true">+</span>';

          card.append(button, quickAdd);
          grid.appendChild(card);
        });

        section.append(heading, grid);
        fragment.appendChild(section);
      });

      elements.palette.replaceChildren(fragment);
      elements.resultCount.textContent = `${matches.length} ${matches.length === 1 ? 'color' : 'colors'}`;
      elements.empty.hidden = matches.length > 0;
      elements.palette.hidden = matches.length === 0;
      renderFamilyButtons();
      renderSelectionHint();
    }

    function selectionMatchesFilter() {
      const color = colorByCode.get(state.code);
      const query = state.search.trim().toUpperCase();
      return Boolean(color)
        && (state.family === 'all' || color.group === state.family)
        && (!query || `${color.code} ${color.family} ${color.group}`.toUpperCase().includes(query));
    }

    function selectionText(key) {
      return (root.dataset[key] || '').replaceAll('__code__', state.code)
        .replaceAll('__size__', state.size === 'mini' ? '2.6 mm Mini' : '5 mm MIDI');
    }

    function renderSelectionHint() {
      const matches = selectionMatchesFilter();
      const label = selectionText('selectionLabel');
      if (elements.selectionHint) {
        elements.selectionHint.textContent = matches ? label : `${root.dataset.filteredLabel} ${label}`;
      }
      const available = Boolean(state.variant?.available) && matches;
      elements.addColor.disabled = !available;
      elements.mobileAdd.disabled = !available;
      elements.addColor.textContent = matches ? selectionText('addFullLabel') : root.dataset.selectLabel;
      elements.mobileAdd.textContent = matches ? selectionText('addLabel') : root.dataset.selectLabel;
      elements.mobileAdd.setAttribute('aria-label', matches ? selectionText('addFullLabel') : root.dataset.filteredLabel);
    }

    function updateUrl() {
      const current = new URL(window.location.href);
      current.searchParams.set('color', state.code);
      current.searchParams.set('size', state.size);
      window.history.replaceState({}, '', current);
    }

    function renderSizeButtons() {
      elements.sizeButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.size === state.size));
      });
      const label = state.size === 'mini' ? '2.6mm Mini' : '5mm Midi';
      elements.selectedSize.textContent = label;
      elements.mobileSize.textContent = label;
      $$('[data-cbp-sets-link]').forEach((link) => {
        link.href = `${root.dataset.routesRoot || '/'}collections/${state.size}-bead-sets`;
      });
      if (elements.pack) {
        elements.pack.textContent = state.size === 'midi'
          ? elements.pack.dataset.packMidi
          : elements.pack.dataset.packMini;
      }
    }

    function setLoading(color) {
      elements.productCard.setAttribute('aria-busy', 'true');
      const hasVisibleImage = !elements.productImage.hidden && Boolean(elements.productImage.getAttribute('src'));
      elements.imageLoading.hidden = hasVisibleImage;
      elements.imageLoading.textContent = 'Loading color…';
      if (!imageForColor(color, state.size) && !hasVisibleImage) {
        elements.productImage.hidden = true;
      }
      elements.productSwatch.style.background = color.hex;
      elements.productFamily.textContent = color.family;
      elements.productTitle.textContent = `${color.code} Single-Color Fuse Beads`;
      elements.productCode.textContent = color.code;
      elements.productPrice.textContent = '—';
      elements.availability.textContent = 'Loading product availability…';
      elements.availability.dataset.available = 'false';
      elements.addColor.disabled = true;
      elements.mobileBar.hidden = false;
      elements.mobileDot.style.background = color.hex;
      elements.mobileCode.textContent = color.code;
      elements.mobilePrice.textContent = '—';
      elements.mobileAdd.disabled = true;
      elements.productError.hidden = true;
      renderSelectionHint();
    }

    async function selectColor(code, { updateHistory = true } = {}) {
      const color = colorByCode.get(code);
      if (!isColorEnabled(color, state.size)) return;
      state.code = code;
      state.product = null;
      state.variant = null;
      const request = ++state.request;
      updateSwatchSelection();
      renderSizeButtons();
      setLoading(color);
      if (updateHistory) updateUrl();

      const paletteImage = imageForColor(color, state.size);
      const paletteImageRequest = paletteImage ? showMainImage(paletteImage, color, request) : null;

      try {
        const product = await getProduct(color);
        if (request !== state.request) return;
        const variant = variantFor(product, state.size);
        const expectedVariant = color.variants?.[state.size];
        if (!variant?.id || String(variant.id) !== String(expectedVariant?.id)) {
          throw new Error(`Variant mapping mismatch for ${color.code} ${state.size}`);
        }
        state.product = product;
        state.variant = variant;
        const image = productImage(product, variant);
        elements.productTitle.textContent = product.title || `${color.code} Single-Color Fuse Beads`;
        elements.productPrice.textContent = money(variant.price);
        elements.mobilePrice.textContent = money(variant.price);
        elements.productLink.href = product.url || `/products/${color.handle}`;
        if (!paletteImageRequest && image) {
          showMainImage(image, color, request);
        }
        const available = Boolean(variant?.available);
        elements.availability.textContent = available ? 'Available to add' : 'Currently unavailable';
        elements.availability.dataset.available = String(available);
        elements.addColor.disabled = !available;
        elements.mobileAdd.disabled = !available;
        elements.productCard.setAttribute('aria-busy', 'false');
        renderSelectionHint();
      } catch (error) {
        if (request !== state.request) return;
        elements.productCard.setAttribute('aria-busy', 'false');
        elements.imageLoading.textContent = 'Product image unavailable';
        elements.availability.textContent = 'Could not load this product.';
        elements.productError.textContent = 'Please try this color again or open its product page.';
        elements.productError.hidden = false;
      }
    }

    function updateSwatchSelection() {
      $$('[data-cbp-palette] [data-select-code]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.selectCode === state.code));
      });
    }

    function putColorInTray(color, size, variant, quantity = 1) {
      if (!color || !variant?.id || variant.available === false) return false;
      const safeQuantity = Math.max(1, Math.min(99, Number.parseInt(quantity, 10) || 1));
      const key = `${color.code}:${size}`;
      const existing = state.tray.get(key);
      const item = {
        key,
        code: color.code,
        hex: color.hex,
        family: color.family,
        size,
        sizeLabel: size === 'mini' ? '2.6mm Mini' : '5mm Midi',
        variantId: variant.id,
        price: Number(variant.price || 0),
        quantity: Math.min(99, (existing?.quantity || 0) + safeQuantity)
      };
      state.tray.set(key, item);
      saveTray();
      renderTray();
      return item;
    }

    async function quickAddColor(button) {
      const code = button.dataset.quickAdd;
      const color = colorByCode.get(code);
      if (!isColorEnabled(color, state.size) || button.disabled) return;
      const selectedSize = state.size;
      const sizeLabel = selectedSize === 'mini' ? '2.6mm Mini' : '5mm Midi';

      button.disabled = true;
      button.classList.remove('cbp__quick-add--success', 'cbp__quick-add--error');
      button.setAttribute('aria-busy', 'true');
      button.innerHTML = '<span class="cbp__quick-add-spinner" aria-hidden="true"></span>';
      elements.cartStatus.textContent = '';

      try {
        const product = await getProduct(color);
        const variant = variantFor(product, selectedSize);
        const expectedVariant = color.variants?.[selectedSize];
        if (!variant?.id || String(variant.id) !== String(expectedVariant?.id)) {
          throw new Error(`Variant mapping mismatch for ${code} ${selectedSize}`);
        }
        if (!variant?.id || variant.available === false) throw new Error(`${code} ${sizeLabel} is unavailable.`);
        const item = putColorInTray(color, selectedSize, variant, 1);
        if (!item) throw new Error(`${code} ${sizeLabel} is unavailable.`);
        button.classList.add('cbp__quick-add--success');
        button.innerHTML = '<span aria-hidden="true">✓</span>';
        elements.cartStatus.textContent = `${code} ${sizeLabel} added to Selected colors.`;
      } catch (error) {
        button.classList.add('cbp__quick-add--error');
        button.innerHTML = '<span aria-hidden="true">!</span>';
        elements.cartStatus.textContent = error.message || `Could not add ${code}. Please try again.`;
      } finally {
        button.removeAttribute('aria-busy');
        window.setTimeout(() => {
          const current = color.variants?.[state.size];
          const currentSize = state.size === 'mini' ? '2.6mm Mini' : '5mm Midi';
          button.classList.remove('cbp__quick-add--success', 'cbp__quick-add--error');
          button.innerHTML = '<span aria-hidden="true">+</span>';
          button.setAttribute('aria-label', `Add ${code} ${currentSize} to selected colors`);
          button.disabled = !isColorEnabled(color, state.size) || current?.available === false;
        }, 1600);
      }
    }

    function addCurrentToTray() {
      if (!selectionMatchesFilter()) return;
      const color = colorByCode.get(state.code);
      const variant = state.variant;
      const expectedVariant = color?.variants?.[state.size];
      if (!isColorEnabled(color, state.size) || !variant?.available || String(variant.id) !== String(expectedVariant?.id)) return;
      const quantity = Math.max(1, Math.min(99, Number.parseInt(elements.quantity.value, 10) || 1));
      elements.quantity.value = String(quantity);
      const item = putColorInTray(color, state.size, variant, quantity);
      if (item) elements.cartStatus.textContent = `${color.code} ${item.sizeLabel} added to Selected colors.`;
    }

    function renderTray() {
      const items = Array.from(state.tray.values());
      const fragment = document.createDocumentFragment();
      items.forEach((item) => {
        const row = document.createElement('li');
        row.className = 'cbp__tray-item';
        row.dataset.key = item.key;

        const dot = document.createElement('span');
        dot.className = 'cbp__tray-dot';
        dot.style.setProperty('--swatch', item.hex);
        dot.setAttribute('aria-hidden', 'true');

        const label = document.createElement('span');
        label.className = 'cbp__tray-label';
        const code = document.createElement('b');
        code.textContent = item.code;
        const size = document.createElement('small');
        size.textContent = `${item.sizeLabel} · ${money(item.price)}`;
        label.append(code, size);

        const quantity = document.createElement('span');
        quantity.className = 'cbp__tray-qty';
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.dataset.action = 'decrease';
        minus.setAttribute('aria-label', `Decrease ${item.code} quantity`);
        minus.textContent = '−';
        const value = document.createElement('span');
        value.textContent = String(item.quantity);
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.dataset.action = 'increase';
        plus.setAttribute('aria-label', `Increase ${item.code} quantity`);
        plus.textContent = '+';
        quantity.append(minus, value, plus);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cbp__tray-remove';
        remove.dataset.action = 'remove';
        remove.setAttribute('aria-label', `Remove ${item.code} ${item.sizeLabel}`);
        remove.textContent = '×';

        row.append(dot, label, quantity, remove);
        fragment.appendChild(row);
      });

      elements.trayList.replaceChildren(fragment);
      elements.trayEmpty.hidden = items.length > 0;
      elements.clearTray.disabled = items.length === 0;
      elements.addCart.disabled = items.length === 0;
      const itemCount = items.length;
      elements.selectionCount.textContent = `${itemCount} ${itemCount === 1 ? 'color' : 'colors'}`;
      elements.mobileTray.textContent = `Tray ${itemCount}`;
      elements.mobileTray.disabled = itemCount === 0;
      const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      elements.subtotal.textContent = items.length ? money(subtotal) : '—';
    }

    function toggleMobileTray(open) {
      const tray = elements.trayList.closest('.cbp__tray');
      tray.classList.toggle('cbp__tray--mobile-open', open);
      elements.mobileTray.setAttribute('aria-expanded', String(open));
    }

    function updateTrayItem(key, action) {
      const item = state.tray.get(key);
      if (!item) return;
      if (action === 'remove') state.tray.delete(key);
      if (action === 'increase') item.quantity = Math.min(99, item.quantity + 1);
      if (action === 'decrease') {
        item.quantity -= 1;
        if (item.quantity <= 0) state.tray.delete(key);
      }
      saveTray();
      renderTray();
    }

    async function addTrayToCart() {
      const items = Array.from(state.tray.values()).map((item) => ({
        id: item.variantId,
        quantity: item.quantity
      }));
      if (!items.length) return;
      elements.addCart.disabled = true;
      elements.addCart.textContent = 'Adding colors…';
      elements.cartStatus.textContent = '';
      try {
        await addItems(items, 'cozybead-palette-tray');
        const addedCount = items.length;
        state.tray.clear();
        saveTray();
        renderTray();
        elements.cartStatus.textContent = `${addedCount} ${addedCount === 1 ? 'color' : 'colors'} added to your cart.`;
      } catch (error) {
        elements.cartStatus.textContent = error.message || 'Could not add the selected colors. Please try again.';
      } finally {
        elements.addCart.textContent = 'Add selected colors to cart';
        elements.addCart.disabled = state.tray.size === 0;
      }
    }

    elements.search.addEventListener('input', () => {
      state.search = elements.search.value;
      elements.searchClear.hidden = !state.search;
      renderPalette();
      updateSwatchSelection();
    });

    elements.searchClear.addEventListener('click', () => {
      elements.search.value = '';
      state.search = '';
      elements.searchClear.hidden = true;
      renderPalette();
      elements.search.focus();
    });

    elements.families.addEventListener('click', (event) => {
      const button = event.target.closest('[data-family]');
      if (!button) return;
      state.family = button.dataset.family;
      renderPalette();
      updateSwatchSelection();
    });

    elements.palette.addEventListener('click', (event) => {
      const quickAdd = event.target.closest('[data-quick-add]');
      if (quickAdd) {
        quickAddColor(quickAdd);
        return;
      }
      const button = event.target.closest('[data-select-code]');
      if (!button) return;
      selectColor(button.dataset.selectCode);
    });

    const preloadHoveredColor = (event) => {
      const button = event.target.closest('[data-select-code]');
      if (!button) return;
      const color = colorByCode.get(button.dataset.selectCode);
      const source = imageForColor(color, state.size);
      if (source) preloadMainImage(source).catch(() => {});
    };
    elements.palette.addEventListener('pointerover', preloadHoveredColor, { passive: true });
    elements.palette.addEventListener('focusin', preloadHoveredColor);

    elements.reset.addEventListener('click', () => {
      state.family = 'all';
      state.search = '';
      elements.search.value = '';
      elements.searchClear.hidden = true;
      renderPalette();
    });

    elements.sizeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.dataset.size === state.size) return;
        state.size = button.dataset.size;
        const sizeColors = colorsForSize(state.size);
        if (!sizeColors.some((color) => color.group === state.family)) state.family = 'all';
        if (!isColorEnabled(colorByCode.get(state.code), state.size)) {
          state.code = sizeColors[0]?.code || 'A1';
        }
        renderSizeButtons();
        renderPalette();
        selectColor(state.code);
      });
    });

    elements.addColor.addEventListener('click', addCurrentToTray);
    elements.mobileAdd.addEventListener('click', addCurrentToTray);
    elements.clearTray.addEventListener('click', () => {
      state.tray.clear();
      saveTray();
      renderTray();
      toggleMobileTray(false);
      elements.cartStatus.textContent = '';
    });
    elements.mobileTray.addEventListener('click', () => {
      const tray = elements.trayList.closest('.cbp__tray');
      toggleMobileTray(!tray.classList.contains('cbp__tray--mobile-open'));
    });
    elements.closeTray.addEventListener('click', () => toggleMobileTray(false));
    elements.trayList.addEventListener('click', (event) => {
      const button = event.target.closest('[data-action]');
      const row = event.target.closest('[data-key]');
      if (!button || !row) return;
      updateTrayItem(row.dataset.key, button.dataset.action);
    });
    elements.addCart.addEventListener('click', addTrayToCart);

    renderPalette();
    renderSizeButtons();
    renderTray();
    selectColor(state.code, { updateHistory: false });
  };

  const boot = () => document.querySelectorAll('[data-cbp-root]').forEach(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();


